import { useEffect } from 'react';
import { useAuth } from 'shared/contexts/AuthContext';
import { ensureMessageBoardUserNameForSession } from 'shared/services/messageBoardUserName';
import { ensureExternalPlatformAccountForSession } from 'domains/experts/application/externalPlatformSession';

/**
 * Repairs, at session start, the onboarding side effects that are fired
 * best-effort and can therefore silently not happen.
 *
 * Both repairs can only be performed by the affected user themselves — the
 * message-board handle because the cloud function provisions its own caller,
 * and the platform account because Firestore rules scope the write to a
 * signed-in user. That is why they live on the client at session start rather
 * than in an admin tool or a backend sweep. Each owns its own once-per-session
 * guard and never throws.
 *
 * Renders nothing; it exists so this work sits in the app layer, which may
 * depend on `domains/`, rather than inside `AuthContext` (in `shared/`, which
 * may not).
 */
export function SessionRepair() {
  const { userAccount, isImpersonating } = useAuth();

  useEffect(() => {
    // While impersonating, `userAccount` is the impersonated expert but the
    // credentials are the admin's. Repairing here would attribute the admin's
    // session to somebody else's account, so sit it out entirely.
    if (!userAccount || isImpersonating) return;
    void ensureMessageBoardUserNameForSession(userAccount);
    void ensureExternalPlatformAccountForSession(userAccount);
  }, [userAccount, isImpersonating]);

  return null;
}
